/**
 * PhaseTabBar — phase selection strip for `phase_dependent` projects.
 *
 * Renders a tab per available phase plus hotkeys:
 *  - 1 / 2 / 3 / 4   : jump to a specific phase (by chronological order)
 *  - `]` / `[`       : cycle next / previous phase
 *  - hold `\`        : flicker mode — rapidly cycle through phases at
 *                      `flickerHzAtom` rate; releases back to the sticky
 *                      active phase on key-up.
 *
 * Hotkeys deliberately avoid Tab and Space: Tab is reserved for browser
 * focus traversal (sidebar, "Done", "Cancel", "Next study" buttons), and
 * Space is the WAI-ARIA button activator. Bracket keys, `\`, and the
 * `1`–`4` jumps are still typeable into text inputs, so the keydown
 * handler skips when focus is in an INPUT/TEXTAREA/contenteditable.
 *
 * The bar is purely a controller — it reads `availableDiscriminatorsAtom` and
 * writes `activeDiscriminatorAtom` / `flickerActiveAtom`. The layout component
 * is responsible for actually swapping the main viewport's series.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  activeDiscriminatorAtom,
  availableDiscriminatorsAtom,
  flickerActiveAtom,
  flickerHzAtom,
} from "../../atoms/gatingAtoms";
import { manipulationSyncEnabledAtom, sliceSyncEnabledAtom } from "../../atoms/viewportSyncAtoms";
import { PHASE_LABEL_SHORT, PHASE_ORDER, type Phase, type Discriminator } from "../../types/gating";

const FLICKER_HZ_OPTIONS = [2, 4, 6, 8, 12] as const;

const FLICKER_KEY = "\\";
const TAB_HOTKEYS = ["1", "2", "3", "4"] as const;

/** Phases in chronological order, filtered to those available in this study. */
function orderAvailablePhases(available: readonly Discriminator[]): Phase[] {
  const phases = new Set(available.filter((d) => d.kind === "phase").map((d) => d.value as Phase));
  return PHASE_ORDER.filter((p) => phases.has(p));
}

export const PhaseTabBar: React.FC = () => {
  const available = useAtomValue(availableDiscriminatorsAtom);
  const [active, setActive] = useAtom(activeDiscriminatorAtom);
  const setFlickerActive = useSetAtom(flickerActiveAtom);
  const [flickerHz, setFlickerHz] = useAtom(flickerHzAtom);

  const phases = useMemo(() => orderAvailablePhases(available), [available]);

  /** Map a phase to its discriminator object, or null if not present. */
  const findDiscriminator = useCallback(
    (phase: Phase) => available.find((d) => d.kind === "phase" && d.value === phase) ?? null,
    [available],
  );

  /** Switch active to a phase, no-op if unavailable. */
  const jumpTo = useCallback(
    (phase: Phase) => {
      const d = findDiscriminator(phase);
      if (d) setActive(d);
    },
    [findDiscriminator, setActive],
  );

  /** Step the active phase chronologically (wraps). */
  const cycleBy = useCallback(
    (step: 1 | -1) => {
      if (phases.length === 0) return;
      const currentPhase = active?.kind === "phase" ? (active.value as Phase) : null;
      const i = currentPhase ? phases.indexOf(currentPhase) : -1;
      const next = phases[(i + step + phases.length) % phases.length];
      jumpTo(next);
    },
    [phases, active, jumpTo],
  );

  // Hotkeys: number jumps, `]`/`[` cycle, hold-`\` to flicker. Bracket keys
  // and `\` aren't browser/widget-bound, but they're still typeable into
  // text fields (lesion notes, flicker-Hz menu search) — skip those so
  // typing `[` doesn't yank the phase out from under the radiologist.
  useEffect(() => {
    const isTypingInField = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingInField(e.target)) return;

      if (e.key === FLICKER_KEY) {
        if (!e.repeat) setFlickerActive(true);
        e.preventDefault();
        return;
      }

      if (e.key === "]") {
        cycleBy(1);
        e.preventDefault();
        return;
      }

      if (e.key === "[") {
        cycleBy(-1);
        e.preventDefault();
        return;
      }

      const idx = TAB_HOTKEYS.indexOf(e.key as (typeof TAB_HOTKEYS)[number]);
      if (idx >= 0 && idx < phases.length) {
        jumpTo(phases[idx]);
        e.preventDefault();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (isTypingInField(e.target)) return;
      if (e.key === FLICKER_KEY) setFlickerActive(false);
    };

    // If the window loses focus while FLICKER_KEY is held, the matching keyup
    // never fires and flicker stays on after focus returns. Force-clear on blur.
    const onWindowBlur = () => setFlickerActive(false);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [phases, cycleBy, jumpTo, setFlickerActive]);

  // No phases → render nothing (caller falls back to a different layout).
  if (phases.length === 0) return null;

  const activePhase = active?.kind === "phase" ? (active.value as Phase) : null;

  return (
    <div className="px-3 py-2">
      <div className="rounded-md bg-surface-2 px-2.5 py-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div
            className="text-[10px] font-semibold text-fg-muted uppercase tracking-wide whitespace-nowrap cursor-help"
            title={
              "Keyboard workflow (eyes-on-image):\n" +
              "  1–4 — jump to phase by order\n" +
              "  ] / [ — cycle next / previous\n" +
              "  hold \\ — flicker through phases at the rate below\n" +
              "  Tab — left to browser focus (not bound)"
            }
          >
            Contrast Phase
          </div>
          <SyncToggles />
        </div>
        <div className="flex items-center gap-1" role="tablist" aria-label="Contrast phase">
          {phases.map((phase, i) => {
            const isActive = phase === activePhase;
            return (
              <button
                key={phase}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => jumpTo(phase)}
                className={[
                  "flex-1 flex items-center justify-center h-6 rounded text-[10px] font-mono transition-colors border",
                  isActive
                    ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
                    : "border-border text-fg-muted hover:bg-surface-2 hover:text-fg",
                ].join(" ")}
              >
                <span className="text-fg-dim mr-0.5">{i + 1}·</span>
                {PHASE_LABEL_SHORT[phase]}
              </button>
            );
          })}
        </div>
        <FlickerHint hz={flickerHz} setHz={setFlickerHz} />
      </div>
    </div>
  );
};

function SyncToggles() {
  const [sliceSync, setSliceSync] = useAtom(sliceSyncEnabledAtom);
  const [manipulationSync, setManipulationSync] = useAtom(manipulationSyncEnabledAtom);
  const base = "h-5 px-0.5 text-[10px] font-mono transition-colors whitespace-nowrap";
  const off = "text-fg-dim hover:text-fg";
  const on = "text-signal-text-tint";
  return (
    <div className="flex items-center gap-2 text-[10px] font-mono">
      <span className="text-fg-dim">sync:</span>
      <button
        type="button"
        aria-pressed={sliceSync}
        title="Sync slice scrolling across phase viewports"
        onClick={() => setSliceSync((v) => !v)}
        className={`${base} ${sliceSync ? on : off}`}
      >
        slice
      </button>
      <button
        type="button"
        aria-pressed={manipulationSync}
        title="Sync pan and zoom across phase viewports"
        onClick={() => setManipulationSync((v) => !v)}
        className={`${base} ${manipulationSync ? on : off}`}
      >
        pan/zoom
      </button>
    </div>
  );
}

function FlickerHint({ hz, setHz }: { hz: number; setHz: (v: number) => void }) {
  const flickerActive = useAtomValue(flickerActiveAtom);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  // Position the dropdown via fixed coordinates so it escapes the panel's
  // overflow-clip. Re-measure when opening or on scroll/resize while open.
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = buttonRef.current?.getBoundingClientRect();
      if (r) setMenuPos({ top: r.bottom + 4, left: r.left + r.width / 2 });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className={[
        "relative text-[10px] font-mono flex items-center justify-between gap-2 whitespace-nowrap pt-0.5",
        flickerActive ? "text-signal-text-tint" : "text-fg-muted",
      ].join(" ")}
    >
      <div className="flex items-center gap-1.5">
        <kbd className="px-1 py-0.5 bg-surface-2 border border-border-strong rounded">\</kbd>
        <span>flicker</span>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          title="Flicker rate"
          className="px-1 py-0.5 rounded hover:bg-white/5 hover:text-white text-[10px] tabular-nums underline-offset-2 hover:underline cursor-pointer"
        >
          {hz} Hz
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <kbd className="px-1 py-0.5 bg-surface-2 border border-border-strong rounded">[</kbd>
        <kbd className="px-1 py-0.5 bg-surface-2 border border-border-strong rounded">]</kbd>
        <span>cycle</span>
      </div>
      {open && menuPos && (
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            transform: "translateX(-50%)",
          }}
          className="z-50 flex flex-col rounded-md border border-border-strong bg-bg-2 shadow-lg py-1 min-w-20"
        >
          {FLICKER_HZ_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              role="option"
              aria-selected={opt === hz}
              onClick={() => {
                setHz(opt);
                setOpen(false);
              }}
              className={[
                "w-full text-left px-3 py-1 text-[11px] tabular-nums hover:bg-white/5 hover:text-white",
                opt === hz ? "text-signal-text-tint" : "text-fg-muted",
              ].join(" ")}
            >
              {opt} Hz
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
